import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {firstValueFrom, Observable} from 'rxjs';
import {DirectusService} from '@frontend/directus';
import {
  DeleteProjectReferenceRequestParams,
  FunctionalScopeInput,
  GetProjectReferencesRequestParams,
  ProjectReferenceCompanyVisibility,
  ProjectReferenceControllerService,
  ProjectReferenceRequest,
  ProjectReferenceResponse,
  ProjectReferenceScoreView,
  UpsertProjectReferenceRequestParams,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {parseLocalDate, toLocalDateString} from '../../../utils/date-utils';
import {AddItemButtonComponent} from '../../buttons/add-item-button/add-item-button.component';
import {NoDataComponent} from '../../no-data/no-data.component';
import {ProjectReferenceCardComponent} from './project-reference-card/project-reference-card.component';
import {ProjectReferencesFilterComponent} from './project-references-filter/project-references-filter.component';
import {
  createEmptyProjectReferenceFilter,
  matchesProjectReferenceFilter,
} from './project-references-filter/project-references-filter.utils';
import {ProjectReferenceFilter} from './project-references-filter/project-references-filter.types';
import {
  FunctionalScopeEntry,
  ProjectReference,
  ProjectReferenceListEntry,
} from './project-references.types';

@Component({
  selector: 'app-project-references',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AddItemButtonComponent,
    NoDataComponent,
    ProjectReferenceCardComponent,
    ProjectReferencesFilterComponent,
  ],
  templateUrl: './project-references.component.html',
})
export class ProjectReferencesComponent {
  private readonly directusService = inject(DirectusService);
  private readonly projectReferenceController = inject(ProjectReferenceControllerService);
  private readonly toastService = inject(ToastService);

  readonly toolId = input.required<number>();
  readonly readOnly = input<boolean>(false);
  readonly scores = input<ProjectReferenceScoreView[]>([]);

  readonly savedCountChange = output<number>();
  readonly unsavedChange = output<boolean>();
  readonly loadingChange = output<boolean>();

  private readonly editedReferenceIds = signal<ReadonlySet<string>>(new Set());

  protected readonly filter = signal<ProjectReferenceFilter>(createEmptyProjectReferenceFilter());

  protected readonly industryOptions = toSignal(
    this.directusService.loadDirectusDropdown('industry'),
    {initialValue: []},
  );

  protected readonly moduleGroups = toSignal(
    this.directusService.loadModuleFunctionOptionsGroupedByModule(),
    {initialValue: []},
  );

  protected readonly functionStatusOptions = toSignal(
    this.directusService.loadDirectusDropdown('project_reference_function_status'),
    {initialValue: []},
  );

  protected readonly countryOptions = toSignal(this.directusService.loadCountryOptions(), {
    initialValue: [],
  });

  protected readonly languageOptions = toSignal(this.directusService.loadLanguageOptions(), {
    initialValue: [],
  });

  private readonly referencesResource = rxResource({
    params: () => this.toolId(),
    stream: ({params: toolId}) => {
      const request: GetProjectReferencesRequestParams = {toolId: toolId};

      return this.projectReferenceController.getProjectReferences(request);
    },
  });

  private readonly isLoading = computed<boolean>(() => this.referencesResource.isLoading());

  protected readonly references = linkedSignal<ProjectReference[]>(() => {
    const responses: ProjectReferenceResponse[] = this.referencesResource.value() ?? [];

    return responses.map((response) => this.toReference(response, crypto.randomUUID()));
  });

  protected readonly referenceCount = computed<number>(() => this.references().length);

  protected readonly hasReferences = computed<boolean>(() => this.referenceCount() > 0);

  protected readonly isEditable = computed<boolean>(() => !this.readOnly());

  private readonly scoreByReferenceId = computed<ReadonlyMap<number, ProjectReferenceScoreView>>(
    () => new Map(this.scores().map((current) => [current.projectReferenceId, current])),
  );

  protected readonly referenceEntries = computed<ProjectReferenceListEntry[]>(() => {
    const filter: ProjectReferenceFilter = this.filter();
    const scores: ProjectReferenceScoreView[] = this.scores();
    const scoreByReferenceId: ReadonlyMap<number, ProjectReferenceScoreView> =
      this.scoreByReferenceId();
    const entries: ProjectReferenceListEntry[] = this.references().map((current) => {
      const entry: ProjectReferenceListEntry = {
        reference: current,
        isHidden: !matchesProjectReferenceFilter(current, filter),
        score: current.id === null ? undefined : scoreByReferenceId.get(current.id),
      };

      return entry;
    });

    return entries.sort(
      (first, second) => this.toScoreRank(scores, first) - this.toScoreRank(scores, second),
    );
  });

  protected readonly hasNoMatchingReferences = computed<boolean>(
    () => this.hasReferences() && this.referenceEntries().every((current) => current.isHidden),
  );

  private readonly savedReferenceCount = computed<number>(
    () => this.references().filter((reference) => reference.id !== null).length,
  );

  private readonly hasUnsavedReferences = computed<boolean>(
    () =>
      this.references().some((reference) => reference.id === null) ||
      this.editedReferenceIds().size > 0,
  );

  constructor() {
    effect(() => {
      this.emitTabStatus();
    });
    effect(() => {
      this.loadingChange.emit(this.isLoading());
    });
  }

  protected addReference(): void {
    this.references.update((references) => [...references, this.createEmptyReference()]);
  }

  protected async saveReference(editedReference: ProjectReference): Promise<void> {
    const projectReferenceRequest: ProjectReferenceRequest = this.toRequest(editedReference);
    const request: UpsertProjectReferenceRequestParams = {
      projectReferenceRequest: projectReferenceRequest,
    };
    const upsert$: Observable<ProjectReferenceResponse> =
      this.projectReferenceController.upsertProjectReference(request);
    const response: ProjectReferenceResponse = await firstValueFrom(upsert$);
    const savedReference: ProjectReference = this.toReference(response, editedReference.localId);

    this.references.update((references) =>
      references.map((reference) =>
        reference.localId === savedReference.localId ? savedReference : reference,
      ),
    );
    void this.toastService.showSuccessMessage();
  }

  protected async removeReference(removedReference: ProjectReference): Promise<void> {
    if (removedReference.id !== null) {
      const request: DeleteProjectReferenceRequestParams = {referenceId: removedReference.id};
      const delete$: Observable<unknown> =
        this.projectReferenceController.deleteProjectReference(request);

      await firstValueFrom(delete$);
    }

    this.references.update((references) =>
      references.filter((reference) => reference.localId !== removedReference.localId),
    );
    this.onReferenceChanges(removedReference.localId, false);
  }

  protected onReferenceChanges(localId: string, hasChanges: boolean): void {
    this.editedReferenceIds.update((editedIds) => {
      const updatedIds = new Set(editedIds);

      if (hasChanges) {
        updatedIds.add(localId);
      } else {
        updatedIds.delete(localId);
      }

      return updatedIds;
    });
  }

  private emitTabStatus(): void {
    this.savedCountChange.emit(this.savedReferenceCount());
    this.unsavedChange.emit(this.hasUnsavedReferences());
  }

  private toScoreRank(
    scores: ProjectReferenceScoreView[],
    entry: ProjectReferenceListEntry,
  ): number {
    return entry.score === undefined ? scores.length : scores.indexOf(entry.score);
  }

  private createEmptyReference(): ProjectReference {
    const reference: ProjectReference = {
      localId: crypto.randomUUID(),
      id: null,
      companyName: '',
      visibility: ProjectReferenceCompanyVisibility.ANONYMIZED,
      projectName: '',
      startDate: null,
      endDate: null,
      headcount: 100,
      industryId: null,
      functionalScope: [],
      countryCodes: [],
      languageCodes: [],
      document: null,
      documentSummary: '',
      comment: '',
    };

    return reference;
  }

  private toReference(response: ProjectReferenceResponse, localId: string): ProjectReference {
    const functionalScope: FunctionalScopeEntry[] = response.functionalScope.map((view) => ({
      functionId: String(view.functionId),
      statusId: view.statusDirectusUuid,
    }));
    const reference: ProjectReference = {
      localId: localId,
      id: response.id,
      companyName: response.companyName,
      visibility: response.companyVisibility,
      projectName: response.projectName,
      startDate: parseLocalDate(response.startDate),
      endDate: parseLocalDate(response.endDate),
      headcount: response.headcount,
      industryId: response.industryDirectusUuid,
      functionalScope: functionalScope,
      countryCodes: response.countryCodes,
      languageCodes: response.languageCodes,
      document: response.document ?? null,
      documentSummary: response.documentSummary ?? '',
      comment: response.comment ?? '',
    };

    return reference;
  }

  private toRequest(reference: ProjectReference): ProjectReferenceRequest {
    if (
      reference.startDate === null ||
      reference.endDate === null ||
      reference.headcount === null ||
      reference.industryId === null
    ) {
      throw new Error('An incomplete project reference cannot be saved');
    }

    const functionalScope: FunctionalScopeInput[] = reference.functionalScope.map((entry) => ({
      functionId: Number(entry.functionId),
      statusDirectusUuid: entry.statusId,
    }));
    const request: ProjectReferenceRequest = {
      id: reference.id ?? undefined,
      toolId: this.toolId(),
      companyName: reference.companyName,
      companyVisibility: reference.visibility,
      projectName: reference.projectName,
      startDate: toLocalDateString(reference.startDate),
      endDate: toLocalDateString(reference.endDate),
      headcount: reference.headcount,
      industryDirectusUuid: reference.industryId,
      functionalScope: functionalScope,
      countryCodes: reference.countryCodes,
      languageCodes: reference.languageCodes,
      documentSummary: reference.documentSummary,
      comment: reference.comment,
    };

    return request;
  }
}
